import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MODES = ['fact', 'align', 'conflict', 'tension'];
const TAG_HINT = 'Simulates the designed tags in AdaptiveCue.';

const NAMED_AVATAR_CLASSES = {
  alice: 'bg-blue-500 text-white ring-2 ring-blue-500/20',
  bob: 'bg-yellow-500 text-white ring-2 ring-yellow-500/20',
  carol: 'bg-green-500 text-white ring-2 ring-green-500/20',
};

const avatarClassesForDisplayName = (displayName) => (
  NAMED_AVATAR_CLASSES[String(displayName || '').trim().toLowerCase()]
  || 'bg-gray-100 text-gray-700 ring-2 ring-gray-200/20'
);

const tagFromMode = (text, mode, viewer) => {
  if (mode === 'align') return { text, align: [viewer], conflict: [], resolution: TAG_HINT };
  if (mode === 'conflict') return { text, align: [], conflict: [viewer], resolution: TAG_HINT };
  if (mode === 'tension') return { text, align: [viewer], conflict: ['Carol'], resolution: TAG_HINT };
  return { text, align: [], conflict: [], resolution: TAG_HINT };
};

const myPolarity = (tag, myName) => {
  const me = String(myName || '').trim().toLowerCase();
  if (!me) return 'neutral';
  if ((tag.align || []).some((name) => String(name || '').trim().toLowerCase() === me)) return 'align';
  if ((tag.conflict || []).some((name) => String(name || '').trim().toLowerCase() === me)) return 'conflict';
  return 'neutral';
};

const AvatarStack = ({ members, side }) => {
  if (!members.length) return null;
  const border = side === 'align' ? 'border-emerald-300' : 'border-red-300';
  return (
    <span className="flex -space-x-1.5" aria-label={members.join(', ')}>
      {members.map((name) => (
        <span
          key={name}
          title={name}
          className={`flex h-5 w-5 items-center justify-center rounded-full border-2 text-[8px] font-bold ${border} ${avatarClassesForDisplayName(name)}`}
        >
          {name.charAt(0).toUpperCase()}
        </span>
      ))}
    </span>
  );
};

const TensionCard = ({ tag, anchorRect, onClose }) => {
  const panelRef = useRef(null);
  const align = tag.align || [];
  const conflict = tag.conflict || [];

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  if (!anchorRect) return null;
  const top = Math.max(8, Math.min(anchorRect.bottom + 6, window.innerHeight - 230));
  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - 296));

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[70] w-72 overflow-hidden rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
      style={{ top, left }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="text-[12px] font-semibold text-gray-900">{tag.text}</div>

      {(align.length > 0 || conflict.length > 0) && (
        <div className="mt-2 flex items-center gap-2" aria-label="Participant positions">
          {align.length > 0 && (
            <span className="flex items-center gap-1">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-50 text-[10px] font-bold text-emerald-600">+</span>
              <AvatarStack members={align} side="align" />
            </span>
          )}
          {align.length > 0 && conflict.length > 0 && (
            <span className="h-px w-4 bg-gray-200" aria-hidden />
          )}
          {conflict.length > 0 && (
            <span className="flex items-center gap-1">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-red-50 text-[10px] font-bold text-red-500">−</span>
              <AvatarStack members={conflict} side="conflict" />
            </span>
          )}
        </div>
      )}

      {tag.resolution && (
        <p className="mt-2.5 text-[11px] font-medium leading-snug text-gray-700">
          {tag.resolution}
        </p>
      )}
    </div>,
    document.body,
  );
};

const chipClass = (polarity, teamStance, interactive) => {
  const base = `inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${
    interactive ? 'cursor-pointer' : 'cursor-default'
  }`;
  const text = polarity === 'align'
    ? 'text-emerald-800'
    : polarity === 'conflict'
      ? 'text-red-800'
      : 'text-gray-600';
  const involved = polarity === 'align' || polarity === 'conflict';
  if (teamStance === 'tension') {
    return involved
      ? `${base} ${text} bg-gradient-to-r from-emerald-50 to-red-50 [box-shadow:inset_2px_0_0_0_#34d399,inset_-2px_0_0_0_#f87171]`
      : `${base} ${text} bg-white [box-shadow:inset_2px_0_0_0_#34d399,inset_-2px_0_0_0_#f87171]`;
  }
  if (teamStance === 'align') {
    return involved
      ? `${base} ${text} bg-emerald-50 ring-1 ring-emerald-200`
      : `${base} ${text} bg-white ring-1 ring-emerald-200`;
  }
  if (teamStance === 'conflict') {
    return involved
      ? `${base} ${text} bg-red-50 ring-1 ring-red-200`
      : `${base} ${text} bg-white ring-1 ring-red-200`;
  }
  return `${base} ${text} bg-white ring-1 ring-gray-200`;
};

const AdaptiveCueTags = ({ texts, interval = 4500, viewer = 'Alice' }) => {
  const labels = useMemo(
    () => (Array.isArray(texts) ? texts.map((text) => String(text || '').trim()).filter(Boolean) : []),
    [texts],
  );
  const [cycle, setCycle] = useState(0);
  const [flashTexts, setFlashTexts] = useState(() => new Set());
  const [openTag, setOpenTag] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const prevSigsRef = useRef(null);
  const chipRefs = useRef(new Map());

  useEffect(() => {
    if (labels.length <= 1) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const timerId = window.setInterval(() => {
      setCycle((current) => current + 1);
    }, interval);

    return () => window.clearInterval(timerId);
  }, [interval, labels.length]);

  const tags = labels.map((text, index) => {
    const mode = MODES[(index + cycle) % MODES.length];
    return { ...tagFromMode(text, mode, viewer), mode };
  });
  const listSignature = tags.map((tag) => `${tag.text}:${tag.mode}`).join('|');

  useEffect(() => {
    const signatures = new Map(tags.map((tag) => [tag.text, tag.mode]));
    const previous = prevSigsRef.current;
    prevSigsRef.current = signatures;
    if (!previous) return undefined;

    const changed = [...signatures.entries()]
      .filter(([text, signature]) => previous.get(text) !== signature)
      .map(([text]) => text);
    if (!changed.length) return undefined;

    setFlashTexts(new Set(changed));
    const timerId = window.setTimeout(() => setFlashTexts(new Set()), 3000);
    return () => window.clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listSignature]);

  const closeTag = () => {
    setOpenTag(null);
    setAnchorRect(null);
  };

  useEffect(() => {
    if (!openTag) return;
    const next = tags.find((tag) => tag.text === openTag.text);
    const element = chipRefs.current.get(openTag.text);
    if (!next || !element) {
      closeTag();
      return;
    }
    setOpenTag((current) => ({ ...next, chipIndex: current?.chipIndex }));
    setAnchorRect(element.getBoundingClientRect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listSignature]);

  if (tags.length === 0) return null;

  return (
    <div className="mb-1.5">
      <span className="inline-flex flex-wrap items-center gap-1">
        {tags.map((tag, index) => {
          const polarity = myPolarity(tag, viewer);
          const hasAlign = (tag.align || []).length > 0;
          const hasConflict = (tag.conflict || []).length > 0;
          const teamStance = hasAlign && hasConflict
            ? 'tension'
            : hasAlign
              ? 'align'
              : hasConflict
                ? 'conflict'
                : 'fact';
          const hasDetails = !!tag.resolution || hasAlign || hasConflict;

          return (
            <button
              key={`${tag.text}-${index}`}
              ref={(element) => {
                if (element) chipRefs.current.set(tag.text, element);
                else chipRefs.current.delete(tag.text);
              }}
              type="button"
              disabled={!hasDetails}
              className={`${chipClass(polarity, teamStance, hasDetails)}${flashTexts.has(tag.text) ? ' tag-flash' : ''}`}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                if (!hasDetails) return;
                const rect = event.currentTarget.getBoundingClientRect();
                if (openTag?.text === tag.text) {
                  closeTag();
                } else {
                  setOpenTag({ ...tag, chipIndex: index });
                  setAnchorRect(rect);
                }
              }}
            >
              {tag.text}
            </button>
          );
        })}
        {openTag && (
          <TensionCard
            tag={openTag}
            anchorRect={anchorRect}
            onClose={closeTag}
          />
        )}
      </span>
    </div>
  );
};

export default AdaptiveCueTags;
